import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { configurePdfUnicodeFont, PDF_UNICODE_FONT } from '../lib/pdfFont';
import { localizeJsPdfInstance, localizePdfText } from '../lib/localization';
import * as XLSX from 'xlsx';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Mail, 
  Phone, 
  MapPin, 
  Trash2, 
  Edit2, 
  Loader2,
  X,
  User,
  Building2,
  Globe,
  CreditCard,
  History,
  ArrowUpRight,
  ArrowDownLeft,
  ArrowLeft,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  TrendingDown,
  FileUp, 
  Paperclip, 
  Download, 
  File as FileIcon, 
  Image as ImageIcon, 
  FileText,
  Tag
} from 'lucide-react';
import { collection, 
  addDoc, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  arrayUnion } from '../firebase';
import { 
  db, 
  auth, 
  storage, 
  ref, 
  uploadBytes, 
  uploadBytesResumable,
  getDownloadURL,
  OperationType,
  handleFirestoreError
} from '../firebase';
import { notify } from '../services/notify';
import { getDefaultCurrency } from '../lib/currency';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import ConfirmModal from './ConfirmModal';
import ContactImport from './ContactImport';
import DocumentChatterPanel from './DocumentChatterPanel';
import { useExclusiveDropdown } from '../lib/dropdowns';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ContactPerson {
  id: string;
  name: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
  isKeyPerson: boolean;
}

interface Attachment {
  name: string;
  url: string;
  type: string;
  size: number;
  createdAt: string;
}

interface Contact {
  id: string;
  name: string;
  emails: string[];
  phones: { number: string; countryCode: string }[];
  email?: string;
  phone?: string;
  type: 'Customer' | 'Supplier' | 'Both';
  status: 'Active' | 'Inactive';
  address?: string;
  website?: string;
  taxNumber?: string;
  tradeLicenseNumber?: string;
  accountNumber?: string;
  currency?: string;
  paymentTerms?: string;
  notes?: string;
  contactPersons: ContactPerson[];
  attachments?: Attachment[];
  imageUrl?: string;
  tags?: string[];
  
  // Addresses
  billingAddress?: string;
  deliveryAddress?: string;
  
  // Sales Defaults
  salesAccount?: string;
  salesTaxRate?: string;
  salesDiscount?: number;
  salesCurrency?: string;
  salesPaymentTerms?: string;
  salesBrandingTheme?: string;
  
  // Purchase Defaults
  purchaseAccount?: string;
  purchaseTaxRate?: string;
  purchasePaymentTerms?: string;

  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
}

interface ContactsViewProps {
  orgId: string;
  initialType?: 'Customer' | 'Supplier' | 'Both' | 'All';
  initialContactId?: string | null;
  onClearInitial?: () => void;
  onNavigate?: (tab: string, id?: string) => void;
  key?: string;

  language?: string;
  orgSettings?: any;
}

interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
}

const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'EGP', 'SAR', 'AED'];
const paymentTerms = ['Due on Receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 60'];
const BRANDING_THEMES = ['Standard', 'Special', 'Modern', 'Classic'];

const hasLocalPostgresSession = () =>
  typeof window !== 'undefined' && !!localStorage.getItem('app_auth_token');

const localApiHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('app_auth_token') || ''}`,
});

const COUNTRY_CODES = [
  { code: '+1', country: 'USA/Canada' },
  { code: '+44', country: 'UK' },
  { code: '+20', country: 'Egypt' },
  { code: '+971', country: 'UAE' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+965', country: 'Kuwait' },
  { code: '+974', country: 'Qatar' },
  { code: '+973', country: 'Bahrain' },
  { code: '+968', country: 'Oman' },
  { code: '+962', country: 'Jordan' },
  { code: '+961', country: 'Lebanon' },
  { code: '+212', country: 'Morocco' },
  { code: '+213', country: 'Algeria' },
  { code: '+216', country: 'Tunisia' },
  { code: '+218', country: 'Libya' },
  { code: '+249', country: 'Sudan' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+34', country: 'Spain' },
  { code: '+39', country: 'Italy' },
  { code: '+91', country: 'India' },
  { code: '+86', country: 'China' },
  { code: '+81', country: 'Japan' },
  { code: '+61', country: 'Australia' },
  { code: '+7', country: 'Russia' },
  { code: '+55', country: 'Brazil' },
  { code: '+52', country: 'Mexico' },
  { code: '+27', country: 'South Africa' },
  { code: '+234', country: 'Nigeria' },
  { code: '+254', country: 'Kenya' },
  { code: '+90', country: 'Turkey' },
  { code: '+82', country: 'South Korea' },
  { code: '+65', country: 'Singapore' },
  { code: '+60', country: 'Malaysia' },
  { code: '+62', country: 'Indonesia' },
  { code: '+66', country: 'Thailand' },
  { code: '+84', country: 'Vietnam' },
  { code: '+63', country: 'Philippines' },
  { code: '+92', country: 'Pakistan' },
  { code: '+880', country: 'Bangladesh' },
  { code: '+94', country: 'Sri Lanka' },
].sort((a, b) => a.country.localeCompare(b.country));

export default function ContactsView({  orgId, orgSettings, initialType = 'All', initialContactId, onClearInitial, onNavigate , language = 'en' }: ContactsViewProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>(initialType);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    city: '',
    taxNumber: '',
    tradeLicenseNumber: '',
    minPayable: '',
    maxPayable: '',
    email: '',
    phone: '',
    accountNumber: '',
    tag: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
  const [statementType, setStatementType] = useState<'Activity' | 'Outstanding'>('Activity');
  const [statementDateRange, setStatementDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [archiveId, setArchiveId] = useState<string | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<'details' | 'history'>('details');
  const [contactDocs, setContactDocs] = useState<any[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'detail' | 'statement'>('list');
  const [taxRates, setTaxRates] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [isAddingNewTag, setIsAddingNewTag] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const organizationCurrency = getDefaultCurrency(orgSettings);

  useEffect(() => {
    if (!orgId) return;

    if (hasLocalPostgresSession()) {
      let cancelled = false;
      void fetch(`/api/organizations/${orgId}/tags`, { headers: localApiHeaders() })
        .then(async response => {
          if (!response.ok) throw new Error(`Failed to load tags: ${response.status}`);
          return response.json();
        })
        .then((tags) => {
          if (!cancelled) setAvailableTags((Array.isArray(tags) ? tags : []).filter(tag => !tag.type || tag.type === 'contacts'));
        })
        .catch(error => {
          if (!cancelled) console.error('Local contact tags load failed:', error);
        });
      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'tags'), where('orgId', '==', orgId), where('type', 'in', ['contacts', null]));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tagList = snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) }));
      // ensure we get contacts tags and old tags without type
      setAvailableTags(tagList.filter(t => !t.type || t.type === 'contacts'));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tags');
    });
    return () => unsubscribe();
  }, [orgId]);

  const [invoiceHistoryTab, setInvoiceHistoryTab] = useState<'invoice' | 'quote' | 'credit_note'>('invoice');
  const [billsHistoryTab, setBillsHistoryTab] = useState<'bill' | 'purchase_order'>('bill');
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [billsSearchTerm, setBillsSearchTerm] = useState('');
  const [showNewDropdown, setShowNewDropdown] = useState(false);
  const announceDropdownOpen = useExclusiveDropdown('contacts', () => setShowNewDropdown(false));

  const [newContact, setNewContact] = useState<Omit<Contact, 'id'>>({
    name: '',
    emails: [''],
    phones: [{ number: '', countryCode: '+1' }],
    type: initialType === 'All' ? 'Customer' : initialType as any,
    status: 'Active',
    address: '',
    website: '',
    taxNumber: '',
    tradeLicenseNumber: '',
    accountNumber: '',
    currency: organizationCurrency,
    paymentTerms: 'Net 30',
    notes: '',
    contactPersons: [],
    tags: [],
    billingAddress: '',
    deliveryAddress: '',
    salesAccount: '',
    salesTaxRate: '0%',
    salesDiscount: 0,
    salesCurrency: organizationCurrency,
    salesPaymentTerms: 'Net 30',
    salesBrandingTheme: 'Standard',
    purchaseAccount: '',
    purchaseTaxRate: '0%',
    purchasePaymentTerms: 'Net 30'
  });

  useEffect(() => {
    const handleQuickAction = (e: any) => {
      if (e.detail?.action === 'new-contact') {
        setIsEditing(false);
        setNewContact({
          name: '',
          emails: [''],
          phones: [{ number: '', countryCode: '+1' }],
          type: 'Customer',
          status: 'Active',
          currency: organizationCurrency,
          address: '',
          website: '',
          taxNumber: '',
          tradeLicenseNumber: '',
          accountNumber: '',
          paymentTerms: 'Net 30',
          notes: '',
          contactPersons: [],
          billingAddress: '',
          deliveryAddress: '',
          salesAccount: '',
          salesTaxRate: '0%',
          salesDiscount: 0,
          salesCurrency: organizationCurrency,
          salesPaymentTerms: 'Net 30',
          salesBrandingTheme: 'Standard',
          purchaseAccount: '',
          purchaseTaxRate: '0%',
          purchasePaymentTerms: 'Net 30'
        });
        setIsModalOpen(true);
      }
    };
    window.addEventListener('QUICK_ACTION', handleQuickAction as EventListener);
    return () => window.removeEventListener('QUICK_ACTION', handleQuickAction as EventListener);
  }, []);

  useEffect(() => {
    if (!orgId) return;

    if (hasLocalPostgresSession()) {
      void Promise.all([
        fetch(`/api/organizations/${orgId}/contacts`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/chart-of-accounts`, { headers: localApiHeaders() }),
        fetch(`/api/organizations/${orgId}/tax-rates`, { headers: localApiHeaders() }),
      ])
        .then(async ([contactsResponse, accountsResponse, taxRatesResponse]) => {
          if (!contactsResponse.ok || !accountsResponse.ok || !taxRatesResponse.ok) {
            throw new Error('Could not load contact setup data');
          }
          const [contactData, accountData, taxRateData] = await Promise.all([
            contactsResponse.json(), accountsResponse.json(), taxRatesResponse.json(),
          ]);
          setContacts(Array.isArray(contactData) ? contactData as Contact[] : []);
          setAccounts(Array.isArray(accountData) ? accountData as Account[] : []);
          setTaxRates(Array.isArray(taxRateData) ? taxRateData : []);
        })
        .catch(error => {
          console.error('Local contacts load failed:', error);
          notify.error('Failed to load contacts');
        })
        .finally(() => setIsLoading(false));
      return;
    }

    const q = query(
      collection(db, 'contacts'),
      where('orgId', '==', orgId),
      orderBy('name', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Contact[];
      setContacts(data);
      setIsLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'contacts');
      setIsLoading(false);
    });

    const accountsQuery = query(
      collection(db, 'chart_of_accounts'),
      where('orgId', '==', orgId),
      orderBy('code', 'asc')
    );
    const unsubAccounts = onSnapshot(accountsQuery, (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Account)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'chart_of_accounts');
    });

    const taxRatesQ = query(
      collection(db, 'tax_rates'),
      where('orgId', '==', orgId)
    );
    const unsubTaxRates = onSnapshot(taxRatesQ, (snapshot) => {
      setTaxRates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tax_rates');
    });

    return () => {
      unsubscribe();
      unsubAccounts();
      unsubTaxRates();
    };
  }, [orgId]);

  useEffect(() => {
    if (initialContactId && contacts.length > 0) {
      const contact = contacts.find(c => c.id === initialContactId);
      if (contact) {
        setSelectedContact(contact);
        setViewMode('detail');
        if (onClearInitial) onClearInitial();
      }
    }
  }, [initialContactId, contacts]);

  useEffect(() => {
    if (selectedContact && (isModalOpen || viewMode === 'detail') && orgId) {
      setIsLoadingDocs(true);

      if (hasLocalPostgresSession()) {
        let cancelled = false;
        void fetch(`/api/organizations/${orgId}/documents?contactId=${encodeURIComponent(selectedContact.id)}`, { headers: localApiHeaders() })
          .then(async response => {
            if (!response.ok) throw new Error(`Failed to load contact documents: ${response.status}`);
            return response.json();
          })
          .then((documents) => {
            if (!cancelled) setContactDocs(Array.isArray(documents) ? documents : []);
          })
          .catch(error => {
            if (!cancelled) {
              console.error('Local contact document load failed:', error);
              setContactDocs([]);
            }
          })
          .finally(() => {
            if (!cancelled) setIsLoadingDocs(false);
          });
        return () => { cancelled = true; };
      }

      const q = query(
        collection(db, 'documents'),
        where('orgId', '==', orgId),
        where('contactId', '==', selectedContact.id),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setContactDocs(docs);
        setIsLoadingDocs(false);
      }, (error) => {
        console.error("Error fetching contact docs:", error);
        setIsLoadingDocs(false);
      });

      return () => unsubscribe();
    }
  }, [selectedContact, isModalOpen, viewMode, orgId]);

  useEffect(() => {
    if (initialType) {
      setTypeFilter(initialType);
      if (initialType !== 'All') {
        setNewContact(prev => ({ ...prev, type: initialType as 'Customer' | 'Supplier' | 'Both' }));
      }
    }
  }, [initialType]);

  const addEmail = () => {
    setNewContact({ ...newContact, emails: [...newContact.emails, ''] });
  };

  const removeEmail = (index: number) => {
    const newEmails = newContact.emails.filter((_, i) => i !== index);
    setNewContact({ ...newContact, emails: newEmails.length > 0 ? newEmails : [''] });
  };

  const updateEmail = (index: number, value: string) => {
    const newEmails = [...newContact.emails];
    newEmails[index] = value;
    setNewContact({ ...newContact, emails: newEmails });
  };

  const addPhone = () => {
    setNewContact({ ...newContact, phones: [...newContact.phones, { number: '', countryCode: '+1' }] });
  };

  const removePhone = (index: number) => {
    const newPhones = newContact.phones.filter((_, i) => i !== index);
    setNewContact({ ...newContact, phones: newPhones.length > 0 ? newPhones : [{ number: '', countryCode: '+1' }] });
  };

  const updatePhone = (index: number, field: 'number' | 'countryCode', value: string) => {
    const newPhones = [...newContact.phones];
    newPhones[index] = { ...newPhones[index], [field]: value };
    setNewContact({ ...newContact, phones: newPhones });
  };

  const addContactPerson = () => {
    const newPerson: ContactPerson = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      email: '',
      phone: '',
      phoneCountryCode: '+1',
      isKeyPerson: newContact.contactPersons.length === 0
    };
    setNewContact({ ...newContact, contactPersons: [...newContact.contactPersons, newPerson] });
  };

  const removeContactPerson = (id: string) => {
    setNewContact({ ...newContact, contactPersons: newContact.contactPersons.filter(p => p.id !== id) });
  };

  const updateContactPerson = (id: string, field: keyof ContactPerson, value: any) => {
    setNewContact({
      ...newContact,
      contactPersons: newContact.contactPersons.map(p => {
        if (p.id === id) {
          return { ...p, [field]: value };
        }
        if (field === 'isKeyPerson' && value === true) {
          return { ...p, isKeyPerson: false };
        }
        return p;
      })
    });
  };

  const handleSaveContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const contactData = {
        ...newContact,
        emails: newContact.emails.filter(e => e.trim() !== ''),
        phones: newContact.phones.filter(p => p.number.trim() !== ''),
        contactPersons: newContact.contactPersons.filter(p => p.name.trim() !== ''),
        updatedAt: serverTimestamp()
      };

      if (hasLocalPostgresSession()) {
        const url = isEditing && selectedContact
          ? `/api/organizations/${orgId}/contacts/${selectedContact.id}`
          : `/api/organizations/${orgId}/contacts`;
        const response = await fetch(url, {
          method: isEditing && selectedContact ? 'PATCH' : 'POST',
          headers: localApiHeaders(),
          body: JSON.stringify(contactData),
        });
        const savedContact = await response.json();
        if (!response.ok) throw new Error(savedContact.error || 'Failed to save contact');
        if (isEditing && selectedContact) {
          setContacts(current => current.map(contact => contact.id === savedContact.id ? savedContact : contact));
          setSelectedContact(savedContact as Contact);
        } else {
          setContacts(current => [...current, savedContact as Contact].sort((a, b) => a.name.localeCompare(b.name)));
        }
      } else if (isEditing && selectedContact) {
        await updateDoc(doc(db, 'contacts', selectedContact.id), contactData);
        setSelectedContact({ ...selectedContact, ...contactData } as Contact);
      } else {
        await addDoc(collection(db, 'contacts'), {
          ...contactData,
          orgId,
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp()
        });
      }
      setIsModalOpen(false);
      setIsEditing(false);
      notify.confirm(isEditing ? "Contact updated" : "Contact created");
    } catch (error) {
      console.error("Error saving contact:", error);
      notify.error("Failed to save contact");
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveContact = async (id: string) => {
    try {
      if (hasLocalPostgresSession()) {
        const response = await fetch(`/api/organizations/${orgId}/contacts/${id}`, {
          method: 'PATCH',
          headers: localApiHeaders(),
          body: JSON.stringify({ status: 'Inactive' }),
        });
        if (!response.ok) throw new Error('Failed to archive contact');
        const savedContact = await response.json();
        setContacts(current => current.map(contact => contact.id === id ? savedContact : contact));
      } else {
        await updateDoc(doc(db, 'contacts', id), { 
          status: 'Inactive',
          updatedAt: serverTimestamp()
        });
      }
      if (selectedContact?.id === id) {
        setSelectedContact({ ...selectedContact, status: 'Inactive' });
      }
      notify.confirm("Contact archived");
      setArchiveId(null);
    } catch (error) {
      console.error("Error archiving contact:", error);
      notify.error("Failed to archive contact");
    }
  };

  const handleGenerateStatement = async (format: 'pdf' | 'excel' | 'email') => {
    if (!selectedContact) return;

    const filteredDocs = contactDocs.filter(doc => {
      const docDate = new Date(doc.date);
      const startDate = new Date(statementDateRange.start);
      const endDate = new Date(statementDateRange.end);
      
      const isInRange = docDate >= startDate && docDate <= endDate;
      
      if (statementType === 'Outstanding') {
        return isInRange && doc.status !== 'Paid' && doc.status !== 'Void' && doc.status !== 'Deleted';
      }
      return isInRange;
    });

    if (format === 'pdf') {
      const doc = localizeJsPdfInstance(new jsPDF());
      await configurePdfUnicodeFont(doc);
      doc.setFontSize(20);
      doc.text('Statement of Account', 14, 22);
      doc.setFontSize(10);
      doc.text(`Contact: ${selectedContact.name}`, 14, 32);
      doc.text(`Period: ${statementDateRange.start} to ${statementDateRange.end}`, 14, 37);
      doc.text(`Type: ${statementType}`, 14, 42);

      const tableData = filteredDocs.map(d => [
        d.date,
        d.number,
        d.type,
        d.status,
        `${d.currency} ${d.totalAmount.toLocaleString()}`
      ]);

      autoTable(doc, {
        startY: 50,
        head: [[localizePdfText('Date'), localizePdfText('Number'), localizePdfText('Type'), localizePdfText('Status'), localizePdfText('Amount')]],
        body: tableData,
        styles: { font: PDF_UNICODE_FONT },
      });

      doc.save(`${selectedContact.name}_Statement.pdf`);
      notify.confirm('Statement downloaded as PDF');
    } else if (format === 'excel') {
      const worksheet = XLSX.utils.json_to_sheet(filteredDocs.map(d => ({
        Date: d.date,
        Number: d.number,
        Type: d.type,
        Status: d.status,
        Amount: d.totalAmount,
        Currency: d.currency
      })));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Statement");
      XLSX.writeFile(workbook, `${selectedContact.name}_Statement.xlsx`);
      notify.confirm('Statement downloaded as Excel');
    } else if (format === 'email') {
      const notificationId = notify.loading(`Sending statement to ${selectedContact.emails[0] || 'contact'}...`);
      setTimeout(() => {
        notify.confirm('Statement sent successfully', { id: notificationId });
        setIsStatementModalOpen(false);
      }, 1500);
    }
  };

  const handleRestoreContact = async (id: string) => {
    try {
      if (hasLocalPostgresSession()) {
        const response = await fetch(`/api/organizations/${orgId}/contacts/${id}`, {
          method: 'PATCH',
          headers: localApiHeaders(),
          body: JSON.stringify({ status: 'Active' }),
        });
        if (!response.ok) throw new Error('Failed to restore contact');
        const savedContact = await response.json();
        setContacts(current => current.map(contact => contact.id === id ? savedContact : contact));
      } else {
        await updateDoc(doc(db, 'contacts', id), { 
          status: 'Active',
          updatedAt: serverTimestamp()
        });
      }
      if (selectedContact?.id === id) {
        setSelectedContact({ ...selectedContact, status: 'Active' });
      }
      notify.confirm("Contact restored");
    } catch (error) {
      console.error("Error restoring contact:", error);
      notify.error("Failed to restore contact");
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !selectedContact) return;
    const file = e.target.files[0];
    
    if (file.size > 30 * 1024 * 1024) {
      notify.error("File is too large. Max size is 30MB");
      e.target.value = '';
      return;
    }

    const toastId = notify.loading(`Uploading ${file.name}...`);
    setIsUploading(true);
    setUploadProgress(10);
    
    try {
      if (hasLocalPostgresSession()) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadResponse = await fetch('/api/files/upload', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('app_auth_token') || ''}`,
          },
          body: formData,
        });
        const uploadResult = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadResult.error || 'Failed to upload file');

        const attachment: Attachment = {
          name: file.name,
          url: uploadResult.url,
          type: file.type,
          size: file.size,
          createdAt: new Date().toISOString(),
        };
        const attachments = [...(selectedContact.attachments || []), attachment];
        const contactResponse = await fetch(`/api/organizations/${orgId}/contacts/${selectedContact.id}`, {
          method: 'PATCH',
          headers: localApiHeaders(),
          body: JSON.stringify({ attachments }),
        });
        const savedContact = await contactResponse.json();
        if (!contactResponse.ok) throw new Error(savedContact.error || 'Failed to update contact');

        setContacts(current => current.map(contact => contact.id === savedContact.id ? savedContact : contact));
        setSelectedContact(savedContact as Contact);
        notify.confirm(`File "${file.name}" attached successfully`, { id: toastId });
        return;
      }

      const storagePath = `contact_attachments/${selectedContact.id}/${Date.now()}_${file.name}`;
      
      let downloadUrl = '';
      try {
        // Try direct upload first with progress
        const fileRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(fileRef, file);
        
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed', 
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setUploadProgress(progress);
            }, 
            (error) => reject(error), 
            () => resolve(null)
          );
        });
        
        downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
      } catch (directError: any) {
        console.warn("Direct upload failed (CORS?), trying proxy...", directError);
        // Fallback to proxy
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetPath', storagePath);
        
        const response = await fetch('/api/proxy/upload', {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          downloadUrl = result.url;
        } else {
          throw directError; // Throw original error if proxy also fails
        }
      }
      
      const attachment: Attachment = {
        name: file.name,
        url: downloadUrl,
        type: file.type,
        size: file.size,
        createdAt: new Date().toISOString()
      };
      
      await updateDoc(doc(db, 'contacts', selectedContact.id), {
        attachments: arrayUnion(attachment),
        updatedAt: serverTimestamp()
      });
      
      setSelectedContact(prev => prev ? {
        ...prev,
        attachments: [...(prev.attachments || []), attachment]
      } : null);
      
      notify.confirm(`File "${file.name}" attached successfully`, { id: toastId });
    } catch (error: any) {
      console.error("Upload error:", error);
      notify.error(`Failed to upload: ${error.message || 'Unknown error'}`, { id: toastId });
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      if (e.target) e.target.value = '';
    }
  };

  const handleProfileImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !selectedContact) return;
    const file = e.target.files[0];
    
    if (!file.type.startsWith('image/')) {
      notify.error("Please upload an image file");
      return;
    }

    const toastId = notify.loading("Updating profile image...");
    setIsUploadingProfile(true);
    
    try {
      if (hasLocalPostgresSession()) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadResponse = await fetch('/api/files/upload', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('app_auth_token') || ''}`,
          },
          body: formData,
        });
        const uploadResult = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadResult.error || 'Failed to upload image');

        const contactResponse = await fetch(`/api/organizations/${orgId}/contacts/${selectedContact.id}`, {
          method: 'PATCH',
          headers: localApiHeaders(),
          body: JSON.stringify({ imageUrl: uploadResult.url }),
        });
        const savedContact = await contactResponse.json();
        if (!contactResponse.ok) throw new Error(savedContact.error || 'Failed to update contact');

        setContacts(current => current.map(contact => contact.id === savedContact.id ? savedContact : contact));
        setSelectedContact(savedContact as Contact);
        notify.confirm("Profile image updated", { id: toastId });
        return;
      }

      const storagePath = `contact_profiles/${selectedContact.id}/${Date.now()}_${file.name}`;
      
      let downloadUrl = '';
      try {
        // Try direct upload with progress
        const fileRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(fileRef, file);
        
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed', 
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setUploadProgress(progress);
            }, 
            (error) => reject(error), 
            () => resolve(null)
          );
        });
        
        downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
      } catch (directError: any) {
        console.warn("Direct profile upload failed, trying proxy...", directError);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetPath', storagePath);
        
        const response = await fetch('/api/proxy/upload', {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          downloadUrl = result.url;
        } else {
          throw directError;
        }
      }
      
      await updateDoc(doc(db, 'contacts', selectedContact.id), {
        imageUrl: downloadUrl,
        updatedAt: serverTimestamp()
      });
      
      setSelectedContact(prev => prev ? { ...prev, imageUrl: downloadUrl } : null);
      notify.confirm("Profile image updated", { id: toastId });
    } catch (error: any) {
      console.error("Profile upload error:", error);
      notify.error("Failed to update profile image", { id: toastId });
    } finally {
      setIsUploadingProfile(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoveAttachment = async (attachmentUrl: string) => {
    if (!selectedContact) return;
    
    try {
      const updatedAttachments = selectedContact.attachments?.filter(a => a.url !== attachmentUrl) || [];

      if (hasLocalPostgresSession()) {
        const response = await fetch(`/api/organizations/${orgId}/contacts/${selectedContact.id}`, {
          method: 'PATCH',
          headers: localApiHeaders(),
          body: JSON.stringify({ attachments: updatedAttachments }),
        });
        const savedContact = await response.json();
        if (!response.ok) throw new Error(savedContact.error || 'Failed to update contact');

        setContacts(current => current.map(contact => contact.id === savedContact.id ? savedContact : contact));
        setSelectedContact(savedContact as Contact);
      } else {
        await updateDoc(doc(db, 'contacts', selectedContact.id), {
          attachments: updatedAttachments,
          updatedAt: serverTimestamp()
        });
        
        // Update local state
        setSelectedContact(prev => prev ? {
          ...prev,
          attachments: updatedAttachments
        } : null);
      }
      
      notify.confirm("Attachment removed");
    } catch (error) {
      console.error("Error removing attachment:", error);
      notify.error("Failed to remove attachment");
    }
  };

  const handleAddNewTag = async () => {
    const name = newTagInput.trim();
    if (!name) return;

    try {
      if (hasLocalPostgresSession()) {
        const response = await fetch(`/api/organizations/${orgId}/tags`, {
          method: 'POST',
          headers: localApiHeaders(),
          body: JSON.stringify({ name, color: '#141414', type: 'contacts' }),
        });
        const tag = await response.json().catch(() => null);
        if (!response.ok) throw new Error(tag?.error || 'Failed to create tag');
        setAvailableTags(current => [...current, tag]);
      } else {
        await addDoc(collection(db, 'tags'), {
          name,
          color: '#141414',
          type: 'contacts',
          orgId,
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp()
        });
      }

      setNewContact(prev => ({ ...prev, tags: [...(prev.tags || []), name] }));
      setIsAddingNewTag(false);
      setNewTagInput('');
      notify.confirm('Tag created successfully');
    } catch (err) {
      if (hasLocalPostgresSession()) console.error('Local contact tag create failed:', err);
      else handleFirestoreError(err, OperationType.CREATE, 'tags');
      notify.error('Failed to create tag');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setNewContact(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tagToRemove) || []
    }));
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (contact.emails && contact.emails.some(e => e.toLowerCase().includes(searchTerm.toLowerCase()))) ||
                         (contact.phones && contact.phones.some(p => p.number.toLowerCase().includes(searchTerm.toLowerCase())));
    
    // Advanced Filters
    const matchesCity = !advancedFilters.city || 
                       (contact.address && contact.address.toLowerCase().includes(advancedFilters.city.toLowerCase())) || 
                       (contact.billingAddress && contact.billingAddress.toLowerCase().includes(advancedFilters.city.toLowerCase()));
    const matchesTax = !advancedFilters.taxNumber || 
                      (contact.taxNumber && contact.taxNumber.toLowerCase().includes(advancedFilters.taxNumber.toLowerCase()));
    const matchesTradeLicense = !advancedFilters.tradeLicenseNumber || 
                      (contact.tradeLicenseNumber && contact.tradeLicenseNumber.toLowerCase().includes(advancedFilters.tradeLicenseNumber.toLowerCase()));
    const matchesEmail = !advancedFilters.email || 
                         (contact.emails && contact.emails.some(e => e.toLowerCase().includes(advancedFilters.email.toLowerCase())));
    const matchesPhone = !advancedFilters.phone || 
                         (contact.phones && contact.phones.some(p => p.number.toLowerCase().includes(advancedFilters.phone.toLowerCase())));
    const matchesAccount = !advancedFilters.accountNumber || 
                           (contact.accountNumber && contact.accountNumber.toLowerCase().includes(advancedFilters.accountNumber.toLowerCase()));
    const matchesTag = !advancedFilters.tag || 
                       (contact.tags && contact.tags.includes(advancedFilters.tag));
    
    const matchesAdvanced = matchesCity && matchesTax && matchesTradeLicense && matchesEmail && matchesPhone && matchesAccount && matchesTag;

    if (typeFilter === 'Archived') {
      return matchesSearch && matchesAdvanced && contact.status === 'Inactive';
    }

    const matchesStatus = contact.status === 'Active';
    const matchesType = typeFilter === 'All' || contact.type === typeFilter || contact.type === 'Both';
    return matchesSearch && matchesAdvanced && matchesStatus && matchesType;
  });

  const financialSummary = React.useMemo(() => {
    const summary = {
      receivable: 0,
      overdue: 0,
      paid: 0,
      payable: 0,
      currency: selectedContact?.currency || 'USD'
    };

    const now = new Date();
    contactDocs.forEach(doc => {
      const amount = doc.totalAmount || 0;
      const isOverdue = doc.dueDate && new Date(doc.dueDate) < now && doc.status !== 'Paid';

      if (doc.type === 'invoice') {
        if (doc.status === 'Paid') {
          summary.paid += amount;
        } else if (doc.status !== 'Void' && doc.status !== 'Deleted' && doc.status !== 'Voided') {
          summary.receivable += amount;
          if (isOverdue) summary.overdue += amount;
        }
      } else if (doc.type === 'bill') {
        if (doc.status === 'Paid') {
          // For bills, paid is still relevant for total paid out? 
          // But the image shows "Collected payments" for Total Paid, which implies invoices.
          // I'll stick to image's context.
        } else if (doc.status !== 'Void' && doc.status !== 'Deleted' && doc.status !== 'Voided') {
          summary.payable += amount;
        }
      }
    });

    return summary;
  }, [contactDocs, selectedContact]);

  return (
    <div className="space-y-6 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
      {viewMode === 'detail' && selectedContact ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Back Button */}
          <button 
            onClick={() => {
              setViewMode('list');
              setSelectedContact(null);
            }}
            className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Contacts
          </button>

          {/* Header Card */}
          <div className="bg-white border border-[#141414] p-8 rounded-[2.5rem] shadow-sm flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="relative group">
                <div 
                  className="w-24 h-24 rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg shadow-indigo-200 overflow-hidden relative"
                  onClick={() => document.getElementById('contact-profile-upload')?.click()}
                >
                  {selectedContact.imageUrl ? (
                    <img src={selectedContact.imageUrl} alt={selectedContact.name} className="w-full h-full object-cover" />
                  ) : (
                    (selectedContact.name || '').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                  )}
                  {isUploadingProfile && (
                    <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center p-2">
                      <Loader2 className="w-8 h-8 text-white animate-spin mb-2" />
                      {uploadProgress !== null && (
                        <span className="text-[10px] text-white font-bold">{uploadProgress}%</span>
                      )}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                    <ImageIcon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <input
                  id="contact-profile-upload"
                  type="file"
                  className="hidden"
                  onChange={handleProfileImageUpload}
                  accept="image/*"
                />
              </div>
              <div className="text-center md:text-left space-y-3">
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                  <h2 className="text-3xl font-bold tracking-tight">{selectedContact.name}</h2>
                  <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold uppercase tracking-widest border border-purple-200">
                    {selectedContact.type}
                  </span>
                    {selectedContact.tags?.map(tag => {
                      const tagObj = availableTags.find(t => t.name === tag);
                      return (
                        <span key={tag} style={{ backgroundColor: tagObj?.color || '#141414', borderColor: tagObj?.color || '#141414' }} className="px-3 py-1 rounded-full text-white text-[10px] font-bold uppercase tracking-widest border flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      )
                    })}
                  <span className={cn(
                    "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border flex items-center gap-1.5",
                    selectedContact.status === 'Active' ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"
                  )}>
                    <div className={cn("w-1.5 h-1.5 rounded-full", selectedContact.status === 'Active' ? "bg-green-500" : "bg-red-500")} />
                    {selectedContact.status}
                  </span>
                </div>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-sm opacity-60">
                  {selectedContact.emails && selectedContact.emails[0] && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {selectedContact.emails[0]}
                    </div>
                  )}
                  {selectedContact.phones && selectedContact.phones.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span className="font-mono">{p.countryCode} {p.number}</span>
                      <span className="text-[10px] uppercase font-bold opacity-40">({idx === 0 ? 'Primary' : 'Mobile'})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  onClick={() => { if (!showNewDropdown) announceDropdownOpen(); setShowNewDropdown(!showNewDropdown); }}
                  className="flex items-center gap-2 px-6 py-3 bg-[#141414] text-[#E4E3E0] rounded-2xl font-bold text-sm hover:opacity-90 transition-all shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  New
                </button>
                {showNewDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-40"
                      onClick={() => setShowNewDropdown(false)}
                    />
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-[#141414]/10 rounded-2xl shadow-lg z-50 overflow-hidden py-2" onClick={() => setShowNewDropdown(false)}>
                      <button 
                        onClick={() => onNavigate?.('quotes', `new-quote-for:${selectedContact.id}`)}
                        className="w-full text-left px-4 py-3 text-sm font-bold hover:bg-[#141414]/5 transition-colors"
                      >
                        New Quotation
                      </button>
                      <button 
                        onClick={() => onNavigate?.('invoices', `new-invoice-for:${selectedContact.id}`)}
                        className="w-full text-left px-4 py-3 text-sm font-bold hover:bg-[#141414]/5 transition-colors"
                      >
                        New Invoice
                      </button>
                      <button 
                        onClick={() => onNavigate?.('purchase_orders', `new-po-for:${selectedContact.id}`)}
                        className="w-full text-left px-4 py-3 text-sm font-bold hover:bg-[#141414]/5 transition-colors"
                      >
                        New Purchase Order
                      </button>
                      <button 
                        onClick={() => onNavigate?.('bills', `new-bill-for:${selectedContact.id}`)}
                        className="w-full text-left px-4 py-3 text-sm font-bold hover:bg-[#141414]/5 transition-colors"
                      >
                        New Bill
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button 
                onClick={() => document.getElementById('contact-attachment-upload')?.click()}
                disabled={isUploading}
                className="flex items-center gap-2 px-6 py-3 bg-white border border-[#141414] rounded-2xl font-bold text-sm hover:bg-[#141414] hover:text-white transition-all shadow-sm disabled:opacity-50"
              >
                <Paperclip className={cn("w-4 h-4", isUploading && "animate-pulse")} />
                {isUploading ? (uploadProgress !== null ? `Uploading ${uploadProgress}%` : 'Uploading...') : 'Attach File'}
              </button>
              <input
                id="contact-attachment-upload"
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept="image/*,application/pdf"
              />
              <button 
                onClick={() => setViewMode('statement')}
                className="flex items-center gap-2 px-6 py-3 bg-white border border-[#141414] rounded-2xl font-bold text-sm hover:bg-[#141414] hover:text-white transition-all shadow-sm"
              >
                <Mail className="w-4 h-4" />
                Send Statement
              </button>
              <button 
                onClick={() => {
                if (!selectedContact) return;
                setIsEditing(true);
                setNewContact({ 
                  id: selectedContact.id,
                  name: selectedContact.name || '',
                  emails: selectedContact.emails && selectedContact.emails.length > 0 ? selectedContact.emails : [selectedContact.email || ''],
                  phones: selectedContact.phones && selectedContact.phones.length > 0 ? selectedContact.phones : [{ number: selectedContact.phone || '', countryCode: '+1' }],
                  type: selectedContact.type || 'Customer',
                  status: selectedContact.status || 'Active',
                  website: selectedContact.website || '',
                  taxNumber: selectedContact.taxNumber || '',
                  tradeLicenseNumber: selectedContact.tradeLicenseNumber || '',
                  accountNumber: selectedContact.accountNumber || '',
                  currency: selectedContact.currency || 'USD',
                  paymentTerms: selectedContact.paymentTerms || 'Net 30',
                  notes: selectedContact.notes || '',
                  contactPersons: selectedContact.contactPersons || [],
                  tags: selectedContact.tags || [],
                  billingAddress: selectedContact.billingAddress || '',
                  deliveryAddress: selectedContact.deliveryAddress || '',
                  salesAccount: selectedContact.salesAccount || '',
                  salesTaxRate: selectedContact.salesTaxRate || '0%',
                  salesDiscount: selectedContact.salesDiscount || 0,
                  salesCurrency: selectedContact.salesCurrency || 'USD',
                  salesPaymentTerms: selectedContact.salesPaymentTerms || 'Net 30',
                  salesBrandingTheme: selectedContact.salesBrandingTheme || 'Standard',
                  purchaseAccount: selectedContact.purchaseAccount || '',
                  purchaseTaxRate: selectedContact.purchaseTaxRate || '0%',
                  purchasePaymentTerms: selectedContact.purchasePaymentTerms || 'Net 30'
                } as any);
                setIsModalOpen(true);
              }}
              className="flex items-center gap-2 px-6 py-3 bg-white border border-[#141414] rounded-2xl font-bold text-sm hover:bg-[#141414] hover:text-white transition-all shadow-sm"
            >
              <Edit2 className="w-4 h-4" />{language === 'ar' ? 'تعديل جهة الاتصال' : 'Edit Contact'}</button>
          </div>
        </div>

        {/* Financial Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                label: 'Total Receivable', 
                value: financialSummary.receivable, 
                desc: 'Invoices awaiting payment', 
                icon: TrendingUp, 
                color: 'blue',
                bg: 'bg-blue-50',
                text: 'text-blue-600',
                border: 'border-blue-100'
              },
              { 
                label: 'Total Overdue', 
                value: financialSummary.overdue, 
                desc: 'Past due date', 
                icon: AlertCircle, 
                color: 'red',
                bg: 'bg-red-50',
                text: 'text-red-600',
                border: 'border-red-100'
              },
              { 
                label: 'Total Paid', 
                value: financialSummary.paid, 
                desc: 'Collected payments', 
                icon: CheckCircle2, 
                color: 'green',
                bg: 'bg-green-50',
                text: 'text-green-600',
                border: 'border-green-100'
              },
              { 
                label: 'Total Payable', 
                value: financialSummary.payable, 
                desc: 'Amount you owe', 
                icon: TrendingDown, 
                color: 'orange',
                bg: 'bg-orange-50',
                text: 'text-orange-600',
                border: 'border-orange-100'
              }
            ].map((card, i) => (
              <div key={i} className={cn("p-6 rounded-[2rem] border shadow-sm space-y-4 bg-white", card.border)}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest opacity-50">{card.label}</span>
                  <div className={cn("p-2 rounded-xl", card.bg, card.text)}>
                    <card.icon className="w-4 h-4" />
                  </div>
                </div>
                <div className="space-y-1">
                  <p className={cn("text-2xl font-bold tracking-tight", card.text)}>
                    {financialSummary.currency} {card.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] opacity-40 font-medium">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Attachments Section */}
          <div className="bg-white border border-[#141414] p-8 rounded-[2.5rem] shadow-sm space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                <h3 className="text-sm font-bold uppercase tracking-widest">Attachments</h3>
              </div>
              <span className="text-[10px] font-bold opacity-40 uppercase tracking-widest">
                {selectedContact.attachments?.length || 0} Files
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {selectedContact.attachments?.map((attachment, idx) => (
                <div key={idx} className="group relative bg-[#141414]/5 rounded-2xl p-4 flex items-center gap-4 hover:bg-[#141414]/10 transition-colors border border-transparent hover:border-[#141414]/10">
                  <div className="p-3 bg-white rounded-xl shadow-sm">
                    {attachment.type.startsWith('image/') ? (
                      <ImageIcon className="w-5 h-5 text-indigo-500" />
                    ) : (
                      <FileText className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate pr-16">{attachment.name}</p>
                    <p className="text-[10px] opacity-40 font-mono">{(attachment.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <div className="absolute top-1/2 right-4 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all scale-90 group-hover:scale-100">
                    <a 
                      href={attachment.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="p-1.5 bg-white rounded-lg shadow-sm hover:bg-indigo-50 text-indigo-600 transition-colors border border-indigo-100"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button 
                      onClick={() => handleRemoveAttachment(attachment.url)} 
                      className="p-1.5 bg-white rounded-lg shadow-sm hover:bg-red-50 text-red-600 transition-colors border border-red-100"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {(!selectedContact.attachments || selectedContact.attachments.length === 0) && (
                <div className="col-span-full py-12 flex flex-col items-center justify-center text-center space-y-4 border-2 border-dashed border-[#141414]/10 rounded-[2rem]">
                  <div className="w-12 h-12 bg-[#141414]/5 rounded-full flex items-center justify-center">
                    <Paperclip className="w-6 h-6 opacity-20" />
                  </div>
                  <div>
                    <p className="text-sm font-bold opacity-40 uppercase tracking-widest">No attachments yet</p>
                    <p className="text-xs opacity-30 mt-1">Upload images or PDFs related to this contact</p>
                  </div>
                  <button 
                    onClick={() => document.getElementById('contact-attachment-upload')?.click()}
                    className="px-6 py-2 bg-[#141414] text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:opacity-90 transition-opacity"
                  >
                    Upload First File
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* History & Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Invoice History */}
            <div className="bg-white border border-[#141414] rounded-[2.5rem] overflow-hidden shadow-sm">
              <div className="p-6 border-b border-[#141414]/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold uppercase tracking-widest">Invoice History</h3>
                  <div className="flex bg-[#141414]/5 p-1 rounded-full">
                    <button 
                      onClick={() => setInvoiceHistoryTab('invoice')}
                      className={cn(
                        "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all",
                        invoiceHistoryTab === 'invoice' ? "bg-[#141414] text-white" : "opacity-40 hover:opacity-100"
                      )}
                    >
                      Invoices
                    </button>
                    <button 
                      onClick={() => setInvoiceHistoryTab('quote')}
                      className={cn(
                        "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all",
                        invoiceHistoryTab === 'quote' ? "bg-[#141414] text-white" : "opacity-40 hover:opacity-100"
                      )}
                    >
                      Quotations
                    </button>
                    <button 
                      onClick={() => setInvoiceHistoryTab('credit_note')}
                      className={cn(
                        "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all",
                        invoiceHistoryTab === 'credit_note' ? "bg-[#141414] text-white" : "opacity-40 hover:opacity-100"
                      )}
                    >
                      Credit Notes
                    </button>
                  </div>
                </div>
                {contactDocs.filter(d => d.type === invoiceHistoryTab).length > 0 && (
                  <div className="mt-4 relative">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-30" />
                    <input 
                      type="text"
                      placeholder={`Search ${invoiceHistoryTab === 'invoice' ? 'invoices' : invoiceHistoryTab === 'quote' ? 'quotations' : 'credit notes'}...`}
                      value={invoiceSearchTerm}
                      onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                      className="w-full bg-[#141414]/5 border-none p-3 pl-12 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-[#141414]/10 transition-all font-medium"
                    />
                  </div>
                )}
                <p className="text-[10px] opacity-40 font-bold text-center mt-4">
                  {`${contactDocs.filter(d => d.type === invoiceHistoryTab).filter(d => d.number?.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) || d.status?.toLowerCase().includes(invoiceSearchTerm.toLowerCase())).length} ${invoiceHistoryTab === 'invoice' ? 'invoices' : invoiceHistoryTab === 'quote' ? 'quotations' : 'credit notes'} found`}
                </p>
              </div>
              <div className="p-6 min-h-[300px]">
                {isLoadingDocs ? (
                  <div className="flex flex-col items-center justify-center py-20 opacity-50">
                    <Loader2 className="w-8 h-8 animate-spin mb-4" />
                    <p className="text-sm italic serif">{`Fetching ${invoiceHistoryTab === 'invoice' ? 'invoices' : invoiceHistoryTab === 'quote' ? 'quotations' : 'credit notes'}...`}</p>
                  </div>
                ) : contactDocs.filter(d => d.type === invoiceHistoryTab).filter(d => d.number?.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) || d.status?.toLowerCase().includes(invoiceSearchTerm.toLowerCase())).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 opacity-30 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-[#141414]/5 flex items-center justify-center">
                      <ArrowUpRight className="w-8 h-8" />
                    </div>
                    <p className="text-sm font-bold">{`No ${invoiceHistoryTab === 'invoice' ? 'invoices' : invoiceHistoryTab === 'quote' ? 'quotations' : 'credit notes'} found for this contact.`}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contactDocs.filter(d => d.type === invoiceHistoryTab).filter(d => d.number?.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) || d.status?.toLowerCase().includes(invoiceSearchTerm.toLowerCase())).map((doc) => (
                      <div key={doc.id} className="bg-[#141414]/5 p-4 rounded-2xl flex items-center justify-between hover:bg-[#141414]/10 transition-all group">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-orange-600 shadow-sm">
                            <ArrowUpRight className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold text-sm">{doc.number}</p>
                            <p className="text-[10px] opacity-50 uppercase font-bold">{doc.date}</p>
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            <p className="font-bold text-sm">{doc.currency} {doc.totalAmount.toLocaleString()}</p>
                            <span className={cn(
                              "text-[10px] font-bold px-2 py-0.5 rounded-full border",
                              doc.status === 'Paid' ? "bg-green-100 text-green-700 border-green-200" : 
                              doc.status === 'Draft' ? "bg-gray-100 text-gray-700 border-gray-200" :
                              "bg-orange-100 text-orange-700 border-orange-200"
                            )}>
                              {doc.status}
                            </span>
                          </div>
                          <button 
                            onClick={() => onNavigate?.(invoiceHistoryTab === 'invoice' || invoiceHistoryTab === 'credit_note' ? 'invoices' : 'quotes', doc.id)}
                            className="p-2 hover:bg-[#141414] hover:text-white rounded-xl transition-all opacity-0 group-hover:opacity-100"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Bills History */}
            <div className="bg-white border border-[#141414] rounded-[2.5rem] overflow-hidden shadow-sm">
              <div className="p-6 border-b border-[#141414]/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold uppercase tracking-widest">Bills History</h3>
                  <div className="flex bg-[#141414]/5 p-1 rounded-full">
                    <button 
                      onClick={() => setBillsHistoryTab('bill')}
                      className={cn(
                        "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all",
                        billsHistoryTab === 'bill' ? "bg-[#141414] text-white" : "opacity-40 hover:opacity-100"
                      )}
                    >
                      Bills
                    </button>
                    <button 
                      onClick={() => setBillsHistoryTab('purchase_order')}
                      className={cn(
                        "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all",
                        billsHistoryTab === 'purchase_order' ? "bg-[#141414] text-white" : "opacity-40 hover:opacity-100"
                      )}
                    >
                      Purchase Orders
                    </button>
                  </div>
                </div>
                {contactDocs.filter(d => d.type === billsHistoryTab).length > 0 && (
                  <div className="mt-4 relative">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-30" />
                    <input 
                      type="text"
                      placeholder={`Search ${billsHistoryTab === 'bill' ? 'bills' : 'purchase orders'}...`}
                      value={billsSearchTerm}
                      onChange={(e) => setBillsSearchTerm(e.target.value)}
                      className="w-full bg-[#141414]/5 border-none p-3 pl-12 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-[#141414]/10 transition-all font-medium"
                    />
                  </div>
                )}
                <p className="text-[10px] opacity-40 font-bold text-center mt-4">
                  {`${contactDocs.filter(d => d.type === billsHistoryTab).filter(d => d.number?.toLowerCase().includes(billsSearchTerm.toLowerCase()) || d.status?.toLowerCase().includes(billsSearchTerm.toLowerCase())).length} ${billsHistoryTab === 'bill' ? 'bills' : 'purchase orders'} found`}
                </p>
              </div>
              <div className="p-6 min-h-[300px]">
                {isLoadingDocs ? (
                  <div className="flex flex-col items-center justify-center py-20 opacity-50">
                    <Loader2 className="w-8 h-8 animate-spin mb-4" />
                    <p className="text-sm italic serif">{`Fetching ${billsHistoryTab === 'bill' ? 'bills' : 'purchase orders'}...`}</p>
                  </div>
                ) : contactDocs.filter(d => d.type === billsHistoryTab).filter(d => d.number?.toLowerCase().includes(billsSearchTerm.toLowerCase()) || d.status?.toLowerCase().includes(billsSearchTerm.toLowerCase())).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 opacity-30 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-[#141414]/5 flex items-center justify-center">
                      <ArrowDownLeft className="w-8 h-8" />
                    </div>
                    <p className="text-sm font-bold">{`No ${billsHistoryTab === 'bill' ? 'bills' : 'purchase orders'} found for this contact.`}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contactDocs.filter(d => d.type === billsHistoryTab).filter(d => d.number?.toLowerCase().includes(billsSearchTerm.toLowerCase()) || d.status?.toLowerCase().includes(billsSearchTerm.toLowerCase())).map((doc) => (
                      <div key={doc.id} className="bg-[#141414]/5 p-4 rounded-2xl flex items-center justify-between hover:bg-[#141414]/10 transition-all group">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-neutral-600 shadow-sm">
                            <ArrowDownLeft className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold text-sm">{doc.number}</p>
                            <p className="text-[10px] opacity-50 uppercase font-bold">{doc.date}</p>
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            <p className="font-bold text-sm">{doc.currency} {doc.totalAmount.toLocaleString()}</p>
                            <span className={cn(
                              "text-[10px] font-bold px-2 py-0.5 rounded-full border",
                              doc.status === 'Paid' ? "bg-green-100 text-green-700 border-green-200" : 
                              doc.status === 'Draft' ? "bg-gray-100 text-gray-700 border-gray-200" :
                              "bg-orange-100 text-orange-700 border-orange-200"
                            )}>
                              {doc.status}
                            </span>
                          </div>
                          <button 
                            onClick={() => onNavigate?.(billsHistoryTab === 'bill' ? 'bills' : 'purchase_orders')}
                            className="p-2 hover:bg-[#141414] hover:text-white rounded-xl transition-all opacity-0 group-hover:opacity-100"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DocumentChatterPanel
            orgId={orgId}
            entityId={selectedContact.id}
            entityType="Contact"
            locale={language === 'ar' ? 'ar' : 'en'}
            className="lg:sticky lg:top-6"
          />

          {/* Contact Persons & Tax Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white border border-[#141414] p-8 rounded-[2.5rem] shadow-sm space-y-6">
              <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Assigned People</h3>
              {selectedContact.contactPersons && selectedContact.contactPersons.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedContact.contactPersons.map((person) => (
                    <div key={person.id} className="bg-[#141414]/5 p-4 rounded-2xl flex items-center justify-between group">
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "w-12 h-12 rounded-full flex items-center justify-center text-white font-bold",
                          person.isKeyPerson ? "bg-[#141414]" : "bg-[#141414]/20"
                        )}>
                          {person.name[0]}
                        </div>
                        <div>
                          <p className="font-bold text-sm flex items-center gap-2">
                            {person.name}
                            {person.isKeyPerson && (
                              <span className="text-[8px] bg-[#141414] text-white px-1.5 py-0.5 rounded-full uppercase tracking-widest">Key</span>
                            )}
                          </p>
                          <p className="text-[10px] opacity-50">{person.email}</p>
                          <p className="text-[10px] opacity-50">{person.phoneCountryCode} {person.phone}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm opacity-30 italic serif">No contact persons assigned.</p>
              )}
            </div>

            <div className="bg-white border border-[#141414] p-8 rounded-[2.5rem] shadow-sm space-y-6">
              <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Tax Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Tax ID / Number</label>
                  <p className="text-sm font-bold mt-1">{selectedContact.taxNumber || 'Not provided'}</p>
                </div>
                {selectedContact.type !== 'Customer' && (
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Trade License Number</label>
                    <p className="text-sm font-bold mt-1">{selectedContact.tradeLicenseNumber || 'Not provided'}</p>
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'رقم الحساب' : 'Account Number'}</label>
                  <p className="text-sm font-bold mt-1">{selectedContact.accountNumber || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Default Currency</label>
                  <p className="text-sm font-bold mt-1">{selectedContact.currency}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : viewMode === 'statement' && selectedContact ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
           {/* Statement Header */}
           <div className="flex items-center justify-between">
              <button 
                onClick={() => setViewMode('detail')}
                className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Back to Contact
              </button>
           </div>
           
           {/* Full Statement View */}
           <div className="bg-white border border-[#141414] rounded-3xl overflow-hidden shadow-sm">
             <div className="p-6 border-b border-[#141414] flex flex-wrap gap-4 items-center justify-between bg-white/50">
               <h2 className="text-xl font-bold tracking-tight">Statement for {selectedContact.name}</h2>
               <div className="flex items-center gap-2">
                 <button onClick={() => handleGenerateStatement('pdf')} className="flex items-center gap-2 px-4 py-2 bg-white border border-[#141414] rounded-xl text-sm font-bold hover:bg-[#141414] hover:text-white transition-colors">
                   Print PDF
                 </button>
                 <button onClick={() => handleGenerateStatement('email')} className="flex items-center gap-2 px-4 py-2 bg-[#141414] text-white rounded-xl text-sm font-bold opacity-90 transition-opacity hover:opacity-100">
                   <Mail className="w-4 h-4" /> Email
                 </button>
               </div>
             </div>

             <div className="p-6 bg-blue-50/30 border-b border-[#141414]/10 flex flex-wrap gap-6 items-end">
               <div className="space-y-2">
                 <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Statement Type</label>
                 <div className="flex gap-2">
                   <button
                     onClick={() => setStatementType('Activity')}
                     className={cn("px-4 py-2 rounded-xl text-sm font-bold border transition-colors", statementType === 'Activity' ? "bg-white border-[#141414] shadow-sm" : "border-transparent opacity-60 hover:bg-white/50 hover:border-[#141414]/20")}
                   >Activity</button>
                   <button
                     onClick={() => setStatementType('Outstanding')}
                     className={cn("px-4 py-2 rounded-xl text-sm font-bold border transition-colors", statementType === 'Outstanding' ? "bg-white border-[#141414] shadow-sm" : "border-transparent opacity-60 hover:bg-white/50 hover:border-[#141414]/20")}
                   >Outstanding</button>
                 </div>
               </div>

               {statementType === 'Activity' ? (
                 <>
                   <div className="space-y-2">
                     <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">From Date</label>
                     <input type="date" value={statementDateRange.start} onChange={e => setStatementDateRange(prev => ({...prev, start: e.target.value}))} className="px-4 py-2 rounded-xl text-sm font-bold border border-[#141414]/10 bg-white" />
                   </div>
                   <div className="space-y-2">
                     <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">To Date</label>
                     <input type="date" value={statementDateRange.end} onChange={e => setStatementDateRange(prev => ({...prev, end: e.target.value}))} className="px-4 py-2 rounded-xl text-sm font-bold border border-[#141414]/10 bg-white" />
                   </div>
                 </>
               ) : (
                 <div className="space-y-2">
                   <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">As At Date</label>
                   <input type="date" value={statementDateRange.end} onChange={e => setStatementDateRange(prev => ({...prev, end: e.target.value}))} className="px-4 py-2 rounded-xl text-sm font-bold border border-[#141414]/10 bg-white" />
                 </div>
               )}
               <button className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition">Update</button>
             </div>

             <div className="p-12 space-y-12">
               <div className="flex justify-between items-start">
                 <div className="space-y-2 max-w-sm">
                   <p className="text-[10px] uppercase tracking-widest font-bold opacity-50">To</p>
                   <p className="font-bold text-xl">{selectedContact.name}</p>
                   <p className="text-sm opacity-60 whitespace-pre-wrap">{selectedContact.billingAddress || selectedContact.address || 'No address provided'}</p>
                 </div>
                 <div className="text-right space-y-4">
                   {statementType === 'Activity' ? (
                      <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-right">
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-50">From Date</div>
                        <div className="text-sm font-bold">{statementDateRange.start}</div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-50">To Date</div>
                        <div className="text-sm font-bold">{statementDateRange.end}</div>
                      </div>
                   ) : (
                      <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-right">
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-50">As At Date</div>
                        <div className="text-sm font-bold">{statementDateRange.end}</div>
                      </div>
                   )}
                 </div>
               </div>

               <div className="border border-[#141414]/10 rounded-2xl overflow-hidden">
                 <div className="overflow-x-auto">
                   <table className="w-full text-sm text-left">
                     <thead className="bg-[#141414]/5 text-[#141414]/60 font-bold uppercase tracking-widest text-[10px]">
                       <tr>
                         <th className="px-6 py-4">Date</th>
                         <th className="px-6 py-4">Activity</th>
                         <th className="px-6 py-4">Reference</th>
                         <th className="px-6 py-4">Due Date</th>
                         <th className="px-6 py-4 text-right">Invoice Amount</th>
                         <th className="px-6 py-4 text-right">Payments</th>
                         <th className="px-6 py-4 text-right">Balance</th>
                       </tr>
                     </thead>
                     <tbody className="divide-y divide-[#141414]/5">
                       {(() => {
                         const filtered = contactDocs.filter(doc => {
                           const docDate = new Date(doc.date);
                           const startDate = new Date(statementDateRange.start);
                           const endDate = new Date(statementDateRange.end);
                           if (statementType === 'Activity') {
                               return docDate >= startDate && docDate <= endDate;
                           } else {
                               return docDate <= endDate && doc.status !== 'Paid' && doc.status !== 'Void' && doc.status !== 'Deleted';
                           }
                         });

                         let runningBalance = 0;
                         return filtered.map((doc, idx) => {
                           const amount = doc.totalAmount || 0;
                           const paid = doc.paymentReceived || 0;
                           
                           runningBalance += amount - paid;

                           return (
                             <tr key={doc.id || idx} className="hover:bg-[#141414]/5 transition-colors">
                               <td className="px-6 py-4 whitespace-nowrap">{doc.date}</td>
                               <td className="px-6 py-4 capitalize">{String(doc?.type || '').replace('_', ' ')}</td>
                               <td className="px-6 py-4 font-mono">{doc.number || doc.reference || '-'}</td>
                               <td className="px-6 py-4">{doc.dueDate || '-'}</td>
                               <td className="px-6 py-4 text-right">{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                               <td className="px-6 py-4 text-right text-red-600">{paid > 0 ? paid.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}</td>
                               <td className="px-6 py-4 text-right font-bold">{runningBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                             </tr>
                           );
                         });
                       })()}
                       {(() => {
                         const filtered = contactDocs.filter(doc => {
                           const docDate = new Date(doc.date);
                           const startDate = new Date(statementDateRange.start);
                           const endDate = new Date(statementDateRange.end);
                           if (statementType === 'Activity') {
                               return docDate >= startDate && docDate <= endDate;
                           } else {
                               return docDate <= endDate && doc.status !== 'Paid' && doc.status !== 'Void' && doc.status !== 'Deleted';
                           }
                         });
                         let rb = 0;
                         filtered.forEach(doc => {
                           rb += (doc.totalAmount || 0) - (doc.paymentReceived || 0);
                         });
                         return (
                           <tr className="bg-[#141414]/5 font-bold">
                             <td colSpan={6} className="px-6 py-4 text-right uppercase tracking-widest text-[10px]">Balance Due {selectedContact.currency}</td>
                             <td className="px-6 py-4 text-right">{rb.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                           </tr>
                         );
                       })()}
                     </tbody>
                   </table>
                 </div>
               </div>

             </div>
           </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{typeFilter === 'Archived' ? 'Archived Contacts' : `${typeFilter}s`}</h1>
          <p className="mt-2 text-sm text-muted-foreground">Manage your {typeFilter.toLowerCase()} relationships and details</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsImportModalOpen(true)}
            className="flex items-center gap-2 border border-border bg-card px-4 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm font-semibold"
          >
            <FileUp className="w-4 h-4" />
            <span>{language === 'ar' ? 'استيراد جهات الاتصال' : 'Import Contacts'}</span>
          </button>
          <button 
            onClick={() => {
              setIsEditing(false);
              setNewContact({
                name: '',
                emails: [''],
                phones: [{ number: '', countryCode: '+1' }],
                type: typeFilter === 'All' || typeFilter === 'Archived' ? 'Customer' : typeFilter as any,
                status: 'Active',
                address: '',
                website: '',
                taxNumber: '',
                tradeLicenseNumber: '',
                accountNumber: '',
                currency: organizationCurrency,
                paymentTerms: 'Net 30',
                notes: '',
                contactPersons: [],
                billingAddress: '',
                deliveryAddress: '',
                salesAccount: '',
                salesTaxRate: '0%',
                salesDiscount: 0,
                salesCurrency: organizationCurrency,
                salesPaymentTerms: 'Net 30',
                salesBrandingTheme: 'Standard',
                purchaseAccount: '',
                purchaseTaxRate: '0%',
                purchasePaymentTerms: 'Net 30'
              });
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span>New Contact</span>
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-card border border-border p-4 rounded-xl shadow-sm">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
            {['All', 'Customer', 'Supplier', 'Both', 'Archived'].map((type) => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                className={cn(
                  "px-3 py-1.5 rounded-md text-xs font-semibold transition-colors whitespace-nowrap border shrink-0",
                  typeFilter === type
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground"
                )}
              >
                {type === 'Archived' ? 'Archived' : `${type}s`}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder={language === 'ar' ? 'بحث عن جهات اتصال...' : 'Search contacts...'}
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button 
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold border transition-colors",
                showAdvancedFilters ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground"
              )}
            >
              <Filter className="w-4 h-4" />
              {showAdvancedFilters ? 'Less filters' : 'More filters'}
            </button>
          </div>
        </div>

        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-card border border-border p-4 rounded-xl shadow-sm animate-in slide-in-from-top-2 duration-300">
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">City / Region</label>
              <input
                type="text"
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-xs focus:border-ring focus:ring-2 focus:ring-ring/20 outline-none font-medium"
                placeholder="Filter by location..."
                value={advancedFilters.city}
                onChange={(e) => setAdvancedFilters({...advancedFilters, city: e.target.value})}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{language === 'ar' ? 'الرقم الضريبي' : 'Tax Number'}</label>
              <input
                type="text"
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-xs focus:border-ring focus:ring-2 focus:ring-ring/20 outline-none font-medium"
                placeholder="Search tax ID..."
                value={advancedFilters.taxNumber}
                onChange={(e) => setAdvancedFilters({...advancedFilters, taxNumber: e.target.value})}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Trade License Number</label>
              <input
                type="text"
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-xs focus:border-ring focus:ring-2 focus:ring-ring/20 outline-none font-medium"
                placeholder="Search license..."
                value={advancedFilters.tradeLicenseNumber}
                onChange={(e) => setAdvancedFilters({...advancedFilters, tradeLicenseNumber: e.target.value})}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</label>
              <input
                type="text"
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-xs focus:border-ring focus:ring-2 focus:ring-ring/20 outline-none font-medium"
                placeholder="Search email..."
                value={advancedFilters.email}
                onChange={(e) => setAdvancedFilters({...advancedFilters, email: e.target.value})}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{language === 'ar' ? 'الهاتف' : 'Phone'}</label>
              <input
                type="text"
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-xs focus:border-ring focus:ring-2 focus:ring-ring/20 outline-none font-medium"
                placeholder="Search phone..."
                value={advancedFilters.phone}
                onChange={(e) => setAdvancedFilters({...advancedFilters, phone: e.target.value})}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{language === 'ar' ? 'رقم الحساب' : 'Account Number'}</label>
              <input
                type="text"
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-xs focus:border-ring focus:ring-2 focus:ring-ring/20 outline-none font-medium"
                placeholder="Filter by account #..."
                value={advancedFilters.accountNumber}
                onChange={(e) => setAdvancedFilters({...advancedFilters, accountNumber: e.target.value})}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{language === 'ar' ? 'الوسوم' : 'Tag'}</label>
              <select
                value={advancedFilters.tag}
                onChange={e => setAdvancedFilters({...advancedFilters, tag: e.target.value})}
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-xs focus:border-ring focus:ring-2 focus:ring-ring/20 outline-none font-medium"
              >
                <option value="">All Tags</option>
                {availableTags.map(tag => (
                  <option key={tag.id} value={tag.name}>{tag.name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 flex items-end justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setAdvancedFilters({
                    city: '',
                    taxNumber: '',
                    tradeLicenseNumber: '',
                    minPayable: '',
                    maxPayable: '',
                    email: '',
                    phone: '',
                    accountNumber: '',
                    tag: ''
                  });
                  setSearchTerm('');
                }}
                className="w-full border border-dashed border-destructive/30 text-destructive hover:bg-destructive/10"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
          <p className="text-sm font-medium text-muted-foreground">Loading contacts...</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="text-center py-20 bg-white/30 border-2 border-dashed border-[#141414]/20 rounded-3xl">
          <User className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-xl font-bold opacity-50 italic serif">No contacts found</p>
          <p className="text-sm opacity-30 mt-2">Add your first contact to start managing relationships</p>
        </div>
      ) : (
        <div className="bg-white/50 border border-[#141414] rounded-3xl overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#141414]/10 bg-[#141414]/5">
                  <th className="p-4 text-[10px] uppercase tracking-widest font-bold opacity-50">Name</th>
                  <th className="p-4 text-[10px] uppercase tracking-widest font-bold opacity-50">Email Address</th>
                  <th className="p-4 text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'الهاتف' : 'Phone'}</th>
                  <th className="p-4 text-[10px] uppercase tracking-widest font-bold opacity-50">Type & Status</th>
                  <th className="p-4 text-[10px] uppercase tracking-widest font-bold opacity-50 text-right">{language === 'ar' ? 'الإجراءات' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr 
                    key={contact.id}
                    onClick={() => {
                      setSelectedContact(contact);
                      setViewMode('detail');
                    }}
                    className="border-b border-[#141414]/5 hover:bg-[#141414]/5 transition-colors cursor-pointer group"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#141414] text-[#E4E3E0] rounded-lg flex items-center justify-center font-bold text-xs shrink-0">
                          {contact.name.charAt(0)}
                        </div>
                        <span className="font-bold text-sm truncate max-w-[200px]">{contact.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-sm opacity-70">
                      {contact.emails && contact.emails.length > 0 ? contact.emails[0] : contact.email || '-'}
                      {contact.emails && contact.emails.length > 1 && (
                        <span className="ml-2 text-[10px] bg-[#141414]/5 px-1.5 py-0.5 rounded-full">+{contact.emails.length - 1}</span>
                      )}
                    </td>
                    <td className="p-4 text-sm opacity-70">
                      {contact.phones && contact.phones.length > 0 
                        ? `${contact.phones[0].countryCode} ${contact.phones[0].number}` 
                        : contact.phone || '-'}
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col items-start gap-1.5">
                        <span className="text-xs font-semibold">{contact.type}</span>
                        <Badge variant={contact.status === 'Active' ? 'success' : 'secondary'} className="text-[10px] uppercase tracking-widest">
                          {contact.status}
                        </Badge>
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedContact(contact);
                            setNewContact({ 
                              name: contact.name || '',
                              emails: contact.emails && contact.emails.length > 0 ? contact.emails : [contact.email || ''],
                              phones: contact.phones && contact.phones.length > 0 ? contact.phones : [{ number: contact.phone || '', countryCode: '+1' }],
                              type: contact.type || 'Customer',
                              status: contact.status || 'Active',
                              website: contact.website || '',
                              taxNumber: contact.taxNumber || '',
                              tradeLicenseNumber: contact.tradeLicenseNumber || '',
                              accountNumber: contact.accountNumber || '',
                              currency: contact.currency || 'USD',
                              paymentTerms: contact.paymentTerms || 'Net 30',
                              notes: contact.notes || '',
                              contactPersons: contact.contactPersons || [],
                              tags: contact.tags || [],
                              billingAddress: contact.billingAddress || '',
                              deliveryAddress: contact.deliveryAddress || '',
                              salesAccount: contact.salesAccount || '',
                              salesTaxRate: contact.salesTaxRate || '0%',
                              salesDiscount: contact.salesDiscount || 0,
                              salesCurrency: contact.salesCurrency || 'USD',
                              salesPaymentTerms: contact.salesPaymentTerms || 'Net 30',
                              salesBrandingTheme: contact.salesBrandingTheme || 'Standard',
                              purchaseAccount: contact.purchaseAccount || '',
                              purchaseTaxRate: contact.purchaseTaxRate || '0%',
                              purchasePaymentTerms: contact.purchasePaymentTerms || 'Net 30'
                            });
                            setIsEditing(true);
                            setActiveModalTab('details');
                            setIsModalOpen(true);
                          }}
                          className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
                          title="Edit Contact"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {contact.status === 'Active' ? (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setArchiveId(contact.id);
                            }}
                            className="p-2 hover:bg-red-500 hover:text-white rounded-full transition-colors text-red-500"
                            title="Archive Contact"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestoreContact(contact.id);
                            }}
                            className="p-2 hover:bg-green-500 hover:text-white rounded-full transition-colors text-green-500"
                            title="Restore Contact"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )}

      {/* Contact Modal */}
      {isModalOpen && (
        <div
          className="app-overlay-inset ui-editor-overlay fixed inset-y-0 end-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="ui-editor-panel bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl max-h-[calc(100dvh-2rem)] rounded-3xl overflow-hidden flex flex-col shadow-2xl">
            <div className="ui-editor-header p-6 border-b border-[#141414] flex items-center justify-between bg-white/50">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-[#141414] text-[#E4E3E0] rounded-2xl flex items-center justify-center font-bold text-2xl shrink-0">
                  {newContact.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">{isEditing ? newContact.name : `New ${typeFilter}`}</h2>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest opacity-50">{newContact.type}</span>
                      <span className="w-1 h-1 bg-[#141414]/20 rounded-full" />
                      <span className={cn(
                        "text-[10px] font-bold uppercase tracking-widest",
                        newContact.status === 'Active' ? "text-green-600" : "text-gray-400"
                      )}>{newContact.status}</span>
                    </div>
                    {isEditing && (
                      <>
                        {newContact.emails[0] && (
                          <div className="flex items-center gap-1 text-[10px] font-bold opacity-40">
                            <Mail className="w-3 h-3" />
                            <span>{newContact.emails[0]}</span>
                          </div>
                        )}
                        {newContact.phones[0]?.number && (
                          <div className="flex items-center gap-1 text-[10px] font-bold opacity-40">
                            <Phone className="w-3 h-3" />
                            <span>{newContact.phones[0].countryCode} {newContact.phones[0].number}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isEditing && (
                  <div className="flex items-center gap-2 bg-[#141414]/5 p-1 rounded-full">
                    <button 
                      onClick={() => setActiveModalTab('details')}
                      className={cn(
                        "px-4 py-1.5 rounded-full text-xs font-bold transition-all",
                        activeModalTab === 'details' ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/10"
                      )}
                    >
                      Details
                    </button>
                    <button 
                      onClick={() => setActiveModalTab('history')}
                      className={cn(
                        "px-4 py-1.5 rounded-full text-xs font-bold transition-all",
                        activeModalTab === 'history' ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/10"
                      )}
                    >
                      History
                    </button>
                  </div>
                )}
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {activeModalTab === 'details' ? (
              <form onSubmit={handleSaveContact} className="ui-editor-form flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                {isEditing && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white/50 border border-[#141414] p-4 rounded-2xl">
                      <p className="text-[10px] uppercase font-bold opacity-40 mb-1">Total Receivable</p>
                      <p className="text-lg font-bold text-orange-600">${financialSummary.receivable.toLocaleString()}</p>
                    </div>
                    <div className="bg-white/50 border border-[#141414] p-4 rounded-2xl">
                      <p className="text-[10px] uppercase font-bold opacity-40 mb-1">Total Overdue</p>
                      <p className="text-lg font-bold text-red-600">${financialSummary.overdue.toLocaleString()}</p>
                    </div>
                    <div className="bg-white/50 border border-[#141414] p-4 rounded-2xl">
                      <p className="text-[10px] uppercase font-bold opacity-40 mb-1">Total Paid</p>
                      <p className="text-lg font-bold text-green-600">${financialSummary.paid.toLocaleString()}</p>
                    </div>
                    <div className="bg-white/50 border border-[#141414] p-4 rounded-2xl">
                      <p className="text-[10px] uppercase font-bold opacity-40 mb-1">Total Payable</p>
                      <p className="text-lg font-bold text-[#141414]">${financialSummary.payable.toLocaleString()}</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="bg-white/30 border border-[#141414] p-6 rounded-3xl space-y-6 shadow-sm">
                      <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Basic Information</h3>
                      
                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Full Name / Company Name *</label>
                        <input 
                          required
                          type="text" 
                          className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                          placeholder="e.g. Acme Corp"
                          value={newContact.name}
                          onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Contact Type *</label>
                          <select 
                            required
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none font-bold"
                            value={newContact.type}
                            onChange={(e) => setNewContact({...newContact, type: e.target.value as any})}
                          >
                            <option value="Customer">Customer</option>
                            <option value="Supplier">Supplier</option>
                            <option value="Both">Both</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Status</label>
                          <select 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none font-bold"
                            value={newContact.status}
                            onChange={(e) => setNewContact({...newContact, status: e.target.value as any})}
                          >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Email Addresses</label>
                          <button type="button" onClick={addEmail} className="text-[10px] font-bold uppercase tracking-widest text-orange-600 hover:opacity-70 transition-opacity flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Add Email
                          </button>
                        </div>
                        {newContact.emails.map((email, index) => (
                          <div key={index} className="flex gap-2">
                            <input 
                              type="email" 
                              className="flex-1 bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                              placeholder="contact@example.com"
                              value={email}
                              onChange={(e) => updateEmail(index, e.target.value)}
                            />
                            {newContact.emails.length > 1 && (
                              <button type="button" onClick={() => removeEmail(index)} className="p-4 text-red-500 hover:bg-red-50 rounded-2xl transition-colors">
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Phone Numbers</label>
                          <button type="button" onClick={addPhone} className="text-[10px] font-bold uppercase tracking-widest text-orange-600 hover:opacity-70 transition-opacity flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Add Phone
                          </button>
                        </div>
                        {newContact.phones.map((phone, index) => (
                          <div key={index} className="flex gap-2 items-center">
                            <select 
                              className="w-32 bg-white/50 border border-[#141414] px-2 py-4 rounded-2xl outline-none focus:bg-white transition-all text-xs font-bold appearance-none"
                              value={phone.countryCode}
                              onChange={(e) => updatePhone(index, 'countryCode', e.target.value)}
                            >
                              {COUNTRY_CODES.map(c => (
                                <option key={c.code} value={c.code}>{c.country} ({c.code})</option>
                              ))}
                            </select>
                            <input 
                              type="tel" 
                              className="flex-1 bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                              placeholder="234 567 890"
                              value={phone.number}
                              onChange={(e) => updatePhone(index, 'number', e.target.value)}
                            />
                            {newContact.phones.length > 1 && (
                              <button type="button" onClick={() => removePhone(index)} className="w-12 h-12 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-2xl transition-colors shrink-0">
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'الموقع الإلكتروني' : 'Website'}</label>
                        <div className="relative">
                          <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30" />
                          <input 
                            type="url" 
                            className="w-full bg-white/50 border border-[#141414] pl-12 pr-4 py-4 rounded-2xl outline-none focus:bg-white transition-all"
                            placeholder="https://example.com"
                            value={newContact.website}
                            onChange={(e) => setNewContact({...newContact, website: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Tags</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {newContact.tags?.map(tag => {
                            const tagObj = availableTags.find(t => t.name === tag);
                            return (
                              <span key={tag} style={{ backgroundColor: tagObj?.color || '#141414' }} className="flex items-center gap-1 px-2 py-1 text-white text-[10px] font-bold rounded-lg uppercase tracking-widest">
                                {tag}
                                <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:opacity-75 transition-colors ml-1">
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                        <div className="relative">
                          {isAddingNewTag ? (
                            <div className="flex gap-2">
                              <input 
                                type="text"
                                value={newTagInput}
                                onChange={(e) => setNewTagInput(e.target.value)}
                                placeholder="Enter tag name..."
                                className="flex-1 bg-white/50 border border-[#141414] px-4 py-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddNewTag();
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={handleAddNewTag}
                                className="px-6 bg-[#141414] text-white rounded-2xl font-bold uppercase tracking-widest hover:opacity-90 transition-opacity"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsAddingNewTag(false);
                                  setNewTagInput('');
                                }}
                                className="px-4 border border-[#141414] text-[#141414] rounded-2xl font-bold uppercase tracking-widest hover:bg-[#141414]/5 transition-all"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <select 
                              className="w-full bg-white/50 border border-[#141414] pl-4 pr-10 py-4 rounded-2xl outline-none focus:bg-white transition-all font-bold appearance-none cursor-pointer"
                              onChange={(e) => {
                                const tag = e.target.value;
                                if (tag === 'ADD_NEW') {
                                  setIsAddingNewTag(true);
                                } else if (tag && !newContact.tags?.includes(tag)) {
                                  setNewContact(prev => ({
                                    ...prev,
                                    tags: [...(prev.tags || []), tag]
                                  }));
                                }
                                e.target.value = '';
                              }}
                              defaultValue=""
                            >
                              <option value="" disabled>Select a tag to add...</option>
                              <option value="ADD_NEW">+ Add New Tag</option>
                              {availableTags.filter(t => !newContact.tags?.includes(t.name)).map(tag => (
                                <option key={tag.id} value={tag.name}>{tag.name}</option>
                              ))}
                            </select>
                          )}
                          {!isAddingNewTag && <Tag className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30 pointer-events-none" />}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Notes</label>
                        <textarea 
                          rows={3}
                          className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all resize-none"
                          placeholder="Internal notes about this contact..."
                          value={newContact.notes}
                          onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="bg-white/30 border border-[#141414] p-6 rounded-3xl space-y-6 shadow-sm">
                      <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Addresses</h3>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Billing Address</label>
                          <textarea 
                            rows={3}
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all resize-none"
                            placeholder="Street, City, Country, Zip Code"
                            value={newContact.billingAddress}
                            onChange={(e) => setNewContact({...newContact, billingAddress: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Delivery Address</label>
                          <textarea 
                            rows={3}
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all resize-none"
                            placeholder="Street, City, Country, Zip Code"
                            value={newContact.deliveryAddress}
                            onChange={(e) => setNewContact({...newContact, deliveryAddress: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="bg-white/30 border border-[#141414] p-6 rounded-3xl space-y-6 shadow-sm">
                      <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Financial Details</h3>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'الرقم الضريبي' : 'Tax Number'}</label>
                          <input 
                            type="text" 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                            placeholder="VAT / Tax ID"
                            value={newContact.taxNumber}
                            onChange={(e) => setNewContact({...newContact, taxNumber: e.target.value})}
                          />
                        </div>
                        {(newContact.type === 'Supplier' || newContact.type === 'Both') && (
                          <div className="space-y-2">
                            <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Trade License Number</label>
                            <input 
                              type="text" 
                              className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                              placeholder="Trade License Number"
                              value={newContact.tradeLicenseNumber}
                              onChange={(e) => setNewContact({...newContact, tradeLicenseNumber: e.target.value})}
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'رقم الحساب' : 'Account Number'}</label>
                          <input 
                            type="text" 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                            placeholder="Bank Account"
                            value={newContact.accountNumber}
                            onChange={(e) => setNewContact({...newContact, accountNumber: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/30 border border-[#141414] p-6 rounded-3xl space-y-6 shadow-sm">
                      <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Sales Defaults</h3>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Default Account</label>
                          <select 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                            value={newContact.salesAccount}
                            onChange={(e) => setNewContact({...newContact, salesAccount: e.target.value})}
                          >
                            <option value="">Select Account</option>
                            {accounts.filter(a => a.type === 'Revenue' || a.type === 'Sales').map(a => (
                              <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Tax Rate</label>
                          <select 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                            value={newContact.salesTaxRate}
                            onChange={(e) => setNewContact({...newContact, salesTaxRate: e.target.value})}
                          >
                            <option value="0%">Tax Exempt (0%)</option>
                            {taxRates.filter(t => t.type !== 'purchases').map(tax => (
                              <option key={tax.id} value={tax.name}>{tax.name} ({tax.rate}%)</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Discount (%)</label>
                          <input 
                            type="number" 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                            placeholder="0"
                            value={newContact.salesDiscount}
                            onChange={(e) => setNewContact({...newContact, salesDiscount: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Currency</label>
                          <select 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                            value={newContact.salesCurrency}
                            onChange={(e) => setNewContact({...newContact, salesCurrency: e.target.value})}
                          >
                            {currencies.map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payment Terms</label>
                          <select 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                            value={newContact.salesPaymentTerms}
                            onChange={(e) => setNewContact({...newContact, salesPaymentTerms: e.target.value})}
                          >
                            {paymentTerms.map(term => (
                              <option key={term} value={term}>{term}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Branding Theme</label>
                          <select 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                            value={newContact.salesBrandingTheme}
                            onChange={(e) => setNewContact({...newContact, salesBrandingTheme: e.target.value})}
                          >
                            {BRANDING_THEMES.map(theme => (
                              <option key={theme} value={theme}>{theme}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/30 border border-[#141414] p-6 rounded-3xl space-y-6 shadow-sm">
                      <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Purchase Defaults</h3>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Default Account</label>
                          <select 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                            value={newContact.purchaseAccount}
                            onChange={(e) => setNewContact({...newContact, purchaseAccount: e.target.value})}
                          >
                            <option value="">Select Account</option>
                            {accounts.filter(a => a.type === 'Expense' || a.type === 'Direct Costs').map(a => (
                              <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Tax Rate</label>
                          <select 
                            className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                            value={newContact.purchaseTaxRate}
                            onChange={(e) => setNewContact({...newContact, purchaseTaxRate: e.target.value})}
                          >
                            <option value="0%">Tax Exempt (0%)</option>
                            {taxRates.filter(t => t.type !== 'sales').map(tax => (
                              <option key={tax.id} value={tax.name}>{tax.name} ({tax.rate}%)</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payment Terms</label>
                        <select 
                          className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none"
                          value={newContact.purchasePaymentTerms}
                          onChange={(e) => setNewContact({...newContact, purchasePaymentTerms: e.target.value})}
                        >
                          {paymentTerms.map(term => (
                            <option key={term} value={term}>{term}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="bg-white/30 border border-[#141414] p-6 rounded-3xl space-y-6 shadow-sm">
                      <div className="flex items-center justify-between border-b border-[#141414]/10 pb-2">
                        <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Contact Persons</h3>
                        <button type="button" onClick={addContactPerson} className="text-[10px] font-bold uppercase tracking-widest text-orange-600 hover:opacity-70 transition-opacity flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Add Person
                        </button>
                      </div>

                      <div className="space-y-4">
                        {newContact.contactPersons.map((person) => (
                          <div key={person.id} className="bg-[#141414]/5 p-4 rounded-2xl space-y-4 relative group">
                            <button 
                              type="button" 
                              onClick={() => removeContactPerson(person.id)}
                              className="absolute top-2 right-2 p-2 text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <label className="text-[8px] uppercase tracking-widest font-bold opacity-50">Name</label>
                                <input 
                                  type="text" 
                                  className="w-full bg-white border border-[#141414]/10 p-2 rounded-xl outline-none focus:border-[#141414] transition-all text-sm"
                                  placeholder="Person Name"
                                  value={person.name}
                                  onChange={(e) => updateContactPerson(person.id, 'name', e.target.value)}
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-[8px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</label>
                                <input 
                                  type="email" 
                                  className="w-full bg-white border border-[#141414]/10 p-2 rounded-xl outline-none focus:border-[#141414] transition-all text-sm"
                                  placeholder="email@example.com"
                                  value={person.email}
                                  onChange={(e) => updateContactPerson(person.id, 'email', e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <label className="text-[8px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'الهاتف' : 'Phone'}</label>
                                <div className="flex gap-1">
                                  <select 
                                    className="w-16 bg-white border border-[#141414]/10 p-1 rounded-xl outline-none focus:border-[#141414] transition-all text-[10px] font-bold appearance-none text-center"
                                    value={person.phoneCountryCode}
                                    onChange={(e) => updateContactPerson(person.id, 'phoneCountryCode', e.target.value)}
                                  >
                                    {COUNTRY_CODES.map(c => (
                                      <option key={c.code} value={c.code}>{c.code}</option>
                                    ))}
                                  </select>
                                  <input 
                                    type="tel" 
                                    className="flex-1 bg-white border border-[#141414]/10 p-2 rounded-xl outline-none focus:border-[#141414] transition-all text-sm"
                                    placeholder="Phone Number"
                                    value={person.phone}
                                    onChange={(e) => updateContactPerson(person.id, 'phone', e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="flex items-end pb-2">
                                <label className="flex items-center gap-2 cursor-pointer group/key">
                                  <div 
                                    onClick={() => updateContactPerson(person.id, 'isKeyPerson', !person.isKeyPerson)}
                                    className={cn(
                                      "w-4 h-4 rounded border flex items-center justify-center transition-all",
                                      person.isKeyPerson ? "bg-[#141414] border-[#141414]" : "border-[#141414]/20 group-hover/key:border-[#141414]"
                                    )}
                                  >
                                    {person.isKeyPerson && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                  </div>
                                  <span className="text-[10px] font-bold uppercase tracking-widest opacity-50 group-hover/key:opacity-100 transition-opacity">Key Person</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                        {newContact.contactPersons.length === 0 && (
                          <div className="text-center py-8 bg-[#141414]/5 rounded-2xl border-2 border-dashed border-[#141414]/10">
                            <p className="text-[10px] font-bold uppercase tracking-widest opacity-30">No contact persons added</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {selectedContact?.id && (
                  <DocumentChatterPanel
                    orgId={orgId}
                    entityId={selectedContact.id}
                    entityType="Contact"
                    locale={language === 'ar' ? 'ar' : 'en'}
                  />
                )}

                <div className="ui-editor-footer pt-8 border-t border-[#141414] flex items-center justify-end gap-4">
                  <button 
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={isSaving}
                    className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    <span>{isEditing ? 'Update Contact' : 'Create Contact'}</span>
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Invoice History */}
                  <div className="space-y-6">
                    <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Invoice History</h3>
                    {isLoadingDocs ? (
                      <div className="flex flex-col items-center justify-center py-10 opacity-50">
                        <Loader2 className="w-6 h-6 animate-spin mb-2" />
                        <p className="text-xs italic serif">Fetching invoices...</p>
                      </div>
                    ) : contactDocs.filter(d => d.type === 'invoice').length === 0 ? (
                      <div className="text-center py-10 bg-[#141414]/5 rounded-2xl border-2 border-dashed border-[#141414]/10">
                        <p className="text-xs font-bold opacity-30">No invoices found</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {contactDocs.filter(d => d.type === 'invoice').map((doc) => (
                          <div key={doc.id} className="bg-white/50 border border-[#141414]/10 p-3 rounded-xl flex items-center justify-between hover:border-[#141414] transition-all group">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
                                <ArrowUpRight className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="font-bold text-xs">{doc.number}</p>
                                <p className="text-[8px] opacity-50 uppercase font-bold">{doc.date}</p>
                              </div>
                            </div>
                            <div className="text-right flex items-center gap-2">
                              <div>
                                <p className="font-bold text-xs">{doc.currency} {doc.totalAmount.toLocaleString()}</p>
                                <span className={cn(
                                  "text-[8px] font-bold px-1.5 py-0.5 rounded-full border",
                                  doc.status === 'Paid' ? "bg-green-100 text-green-700 border-green-200" : 
                                  doc.status === 'Draft' ? "bg-gray-100 text-gray-700 border-gray-200" :
                                  "bg-orange-100 text-orange-700 border-orange-200"
                                )}>
                                  {doc.status}
                                </span>
                              </div>
                              <button 
                                onClick={() => {
                                  if (onNavigate) {
                                    onNavigate('invoices');
                                  }
                                }}
                                className="p-1.5 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all opacity-0 group-hover:opacity-100"
                                title="Edit Document"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Bills History */}
                  <div className="space-y-6">
                    <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Bills History</h3>
                    {isLoadingDocs ? (
                      <div className="flex flex-col items-center justify-center py-10 opacity-50">
                        <Loader2 className="w-6 h-6 animate-spin mb-2" />
                        <p className="text-xs italic serif">Fetching bills...</p>
                      </div>
                    ) : contactDocs.filter(d => d.type === 'bill').length === 0 ? (
                      <div className="text-center py-10 bg-[#141414]/5 rounded-2xl border-2 border-dashed border-[#141414]/10">
                        <p className="text-xs font-bold opacity-30">No bills found</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {contactDocs.filter(d => d.type === 'bill').map((doc) => (
                          <div key={doc.id} className="bg-white/50 border border-[#141414]/10 p-3 rounded-xl flex items-center justify-between hover:border-[#141414] transition-all group">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-neutral-100 text-[#141414] flex items-center justify-center">
                                <ArrowDownLeft className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="font-bold text-xs">{doc.number}</p>
                                <p className="text-[8px] opacity-50 uppercase font-bold">{doc.date}</p>
                              </div>
                            </div>
                            <div className="text-right flex items-center gap-2">
                              <div>
                                <p className="font-bold text-xs">{doc.currency} {doc.totalAmount.toLocaleString()}</p>
                                <span className={cn(
                                  "text-[8px] font-bold px-1.5 py-0.5 rounded-full border",
                                  doc.status === 'Paid' ? "bg-green-100 text-green-700 border-green-200" : 
                                  doc.status === 'Draft' ? "bg-gray-100 text-gray-700 border-gray-200" :
                                  "bg-orange-100 text-orange-700 border-orange-200"
                                )}>
                                  {doc.status}
                                </span>
                              </div>
                              <button 
                                onClick={() => {
                                  if (onNavigate) {
                                    onNavigate('bills');
                                  }
                                }}
                                className="p-1.5 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all opacity-0 group-hover:opacity-100"
                                title="Edit Document"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Import Contacts Modal */}
      {isImportModalOpen && (
        <ContactImport 
          orgId={orgId}
          orgSettings={orgSettings}
          onClose={() => setIsImportModalOpen(false)}
          onComplete={() => {
            setIsImportModalOpen(false);
          }}
        />
      )}

  <ConfirmModal 
        isOpen={!!archiveId}
        onClose={() => setArchiveId(null)}
        onConfirm={() => archiveId && handleArchiveContact(archiveId)}
        title="Archive Contact"
        message="Are you sure you want to archive this contact? It will be moved to the Archived section."
        confirmText="Archive"
      />

    </div>
  );
}
